/**
 * キャラクターシートから抽出した俳優ポーズ (prompt_model_posesの有効世代) の型と表示ヘルパー。
 *
 * docs/features/character-sheet-pose-extraction-plan.md §2.1 / §5。
 * BE (pose_extraction_service.attach_pose_info) が俳優一覧の各行に `poses` と
 * `pose_extraction_state` を足す。機能フラグ (POSE_EXTRACTION_ENABLED) がoffの間は
 * キー自体が付かないので、型はどちらもoptionalにして「キーが無い = 何も表示しない」と扱う。
 */

/** シート上の節 (三面図 / 表情 / 動作 / 不明) */
export type ActorPoseSection = 'turnaround' | 'expression' | 'pose' | 'unknown';

/** 1体分のポーズ (背景透過PNG)。 */
export type ActorPose = {
  id: string;
  section: ActorPoseSection;
  /** 正準ラベル (英語の固定語彙。front / happy / running等) */
  label: string;
  /** 表示用の日本語 (例: 正面 / 喜び / 走る) */
  label_ja: string;
  /** Geminiの日本語の自由記述 */
  description: string | null;
  width: number;
  height: number;
  /** 全身の立ち絵ならtrue・顔のアップ (表情シート) ならfalse */
  is_full_body: boolean;
  /** シート上の読み順 (上段左から) */
  sort_order: number;
  /** 公開URL (取得失敗時はnull) */
  public_url: string | null;
};

/** 最新の要求の世代の状態 (prompt_model_pose_extractions.state)。要求が無ければnull */
export type ActorPoseExtractionState =
  | 'queued'
  | 'building'
  | 'ready'
  | 'active'
  | 'superseded'
  | 'failed'
  | 'skipped';

/** 俳優行に付くポーズ関連のフィールド (各画面の俳優型に交差させる) */
export type ActorPoseFields = {
  poses?: ActorPose[];
  pose_extraction_state?: ActorPoseExtractionState | null;
  /** 最新の要求の世代id (prompt_models.character_sheet_pose_request_id)。再抽出直後の表示上書きを外す判定に使う */
  pose_extraction_request_id?: string | null;
};

/** 抽出が進行中 (投入済み / 保存中 / 切り替え待ち) か */
export function isPoseExtractionInProgress(state: ActorPoseExtractionState | null | undefined): boolean {
  return state === 'queued' || state === 'building' || state === 'ready';
}

/**
 * 状態ラベル。表示不要ならnull。
 * ポーズが無いときは「抽出中 / 失敗」、既にポーズがあるときは再抽出の進行・失敗として出す。
 */
export function getPoseExtractionStateLabel(
  state: ActorPoseExtractionState | null | undefined,
  hasPoses: boolean,
): string | null {
  if (isPoseExtractionInProgress(state)) return hasPoses ? '再抽出中' : 'ポーズを抽出中';
  if (state === 'failed') return hasPoses ? '再抽出に失敗' : 'ポーズの抽出に失敗';
  return null;
}

/**
 * サムネイルのキャプション。同じlabelが複数あればsort_order順に「走る(1)」「走る(2)」と番号を振る。
 * 戻り値はposesと同じ並び。
 */
export function getPoseCaptions(poses: ActorPose[]): string[] {
  const totals = new Map<string, number>();
  for (const p of poses) totals.set(p.label, (totals.get(p.label) ?? 0) + 1);
  const seen = new Map<string, number>();
  return poses.map((p) => {
    const name = p.label_ja || p.label;
    if ((totals.get(p.label) ?? 0) < 2) return name;
    const n = (seen.get(p.label) ?? 0) + 1;
    seen.set(p.label, n);
    return `${name}(${n})`;
  });
}
